<template>
  <v-data-table :headers="headers"
                :items="teams"
                :items-per-page="itemsPerPage"
                :loading="loading"
                :dense="dense"
                :hide-default-footer="hideDefaultFooter"
                loading-text="Loading teams...">
    <template v-slot:item.name="{item: team}">
      <template v-if="hideDetails">
        {{ '*'.repeat(20) }}
      </template>
      <template v-else>
        {{ team.name }}
      </template>
    </template>
    <template v-slot:item.licensed_users="{item: team}">
      <div class="text-end">
        <template v-if="hideDetails">
          {{ '*'.repeat(20) }}
        </template>
        <template v-else>
          {{ team.licensed_users }} / {{ team.max_users }}
        </template>
      </div>
    </template>

    <template v-slot:item.storage_used="{item: team}">
      <div class="text-end">
        <template v-if="hideDetails">
          {{ '*'.repeat(20) }}
        </template>
        <template v-else>
          {{ team.storage_used | filesize(2) }}
        </template>
      </div>
    </template>

    <template v-slot:item.license_end_date="{item: team}">
      <div class="text-end">
        <template v-if="hideDetails">
          {{ '*'.repeat(20) }}
        </template>
        <template v-else>
          <template v-if="team.is_license_active">
            {{ team.license_end_date | datetime('YYYY-MM-DD') }}
          </template>
          <v-chip v-else
                  color="error"
                  dark
                  label
                  class="white--text">
            {{ team.license_end_date | datetime('YYYY-MM-DD') }}
          </v-chip>
        </template>
      </div>
    </template>

    <template v-slot:item.actions="{item: team}">
      <v-menu tile
              bottom
              left
              offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs"
                 v-on="on"
                 small
                 tile
                 block
                 :color="team.id === user.current_team_id ? 'success' : ''">
            {{ team.id === user.current_team_id ? 'Current' : 'Actions' }}
            <v-icon right>
              fal fa-fw fa-chevron-down
            </v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item :disabled="!team.is_license_active || form.busy || user.current_team_id === team.id"
                       @click.prevent="switchToTeam(team)">
            <v-list-item-title>Switch</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isEmployee"
                       :disabled="!team.is_license_active || !team.search_enabled || form.busy"
                       @click.prevent="indexContacts(team)">
            <v-list-item-title>Index contacts</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isEmployee"
                       :disabled="!team.is_license_active || !team.search_enabled || form.busy"
                       @click.prevent="indexPresentations(team)">
            <v-list-item-title>Index presentations</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import userService from "../../../services/userService";
import searchService from '../../../../../ml/services/searchService';
import DOMPurify from 'dompurify';

export default {
  props: {
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDefaultFooter: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 50,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    teams: {
      type: Array,
      required: true,
    }
  },
  data() {
    return {
      form: new Form({})
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'isEmployee',
      'user',
    ]),
    headers() {
      if (this.isEmployee) {
        return [
          {
            text: 'ID',
            value: 'id',
            filterable: true,
          },
          {text: 'Team', value: 'name', filterable: true,},
          {text: 'Users', value: 'licensed_users', filterable: true, align: 'end',},
          {text: 'Storage', value: 'storage_used', filterable: true, align: 'end',},
          {text: 'Subscription Ends', value: 'license_end_date', filterable: true, align: 'end'},
          {text: 'Actions', value: 'actions', filterable: false, sortable: false,},
        ]
      }
      return [
        {text: 'Team', value: 'name', filterable: true,},
        //{text: 'Type', value: 'team_type', filterable: true,},
        {text: 'Users', value: 'licensed_users', filterable: true, align: 'end',},
        {text: 'Storage', value: 'storage_used', filterable: true, align: 'end',},
        {text: 'Subscription Ends', value: 'license_end_date', filterable: true, align: 'end',},
        {text: 'Actions', value: 'actions', filterable: false, sortable: false,},
      ]
    }
  },
  methods: {
    indexContacts(team) {
      if (confirm(`Are you sure you want to index the contacts for ${DOMPurify.sanitize(team.name)}?`)) {
        searchService.indexContacts(team.id)
          .then(() => {
            this.$toast.success(`Indexing contacts for team ${DOMPurify.sanitize(team.name)} (${team.id})`);
          })
          .catch(err => {
            if (err.response && err.response.data) {
              this.$toast.error(DOMPurify.sanitize(err.response.data.message));
            } else {
              this.$toast.error(DOMPurify.sanitize(err.message));
            }
          })
      }
    },
    indexPresentations(team) {
      if (confirm(`Are you sure you want to index the presentations for ${DOMPurify.sanitize(team.name)}?`)) {
        searchService.indexPresentations(team.id)
          .then(() => {
            this.$toast.success(`Indexing presentations for team ${DOMPurify.sanitize(team.name)} (${team.id})`);
          })
          .catch(err => {
            if (err.response && err.response.data) {
              this.$toast.error(DOMPurify.sanitize(err.response.data.message));
            } else {
              this.$toast.error(DOMPurify.sanitize(err.message));
            }
          })
      }
    },
    configureSearchIndexes(team) { // MLD-4320
      if (confirm(`Are you sure you want to re-index contacts and presentations for ${DOMPurify.sanitize(team.name)}? This could take a while.`)) {
        searchService.indexTeam(team.id)
          .then(() => {
            this.$toast.success(`Indexing team ${DOMPurify.sanitize(team.name)} (${team.id})`);
          })
          .catch(err => {
            if (err.response && err.response.data) {
              this.$toast.error(DOMPurify.sanitize(err.response.data.message));
            } else {
              this.$toast.error(DOMPurify.sanitize(err.message));
            }
          })
      }
    },
    switchToTeam(team) {
      if (this.loading) {
        return;
      }
      if (this.form.busy) {
        return;
      }
      this.form.startProcessing();
      userService.switchUserToTeam(this.user.id, team.id)
        .then(() => {
          window.localStorage.clear(); // MLD-6465
          window.sessionStorage.clear();
          this.$emit('updated');
          this.$toast.success(`Switched to ${DOMPurify.sanitize(team.name)}`);
          location.reload();
        })
        .finally(() => {
          this.form.busy = false;
        })
    }
  }

}
</script>
